import { useState, useEffect, useCallback, type ReactNode } from 'react'
import { Trophy, Medal, Crown, Loader, AlertCircle, RefreshCw, Award, ShieldCheck } from 'lucide-react'
import { useLang } from '../i18n/LangContext'
import { apiGet } from '../services/api'

// Server-aggregated ranking row (matches /api/leaderboard response shape).
interface AgentRanking {
  agent_name: string
  total_score: number
  sessions: number
  avg_score: number
  best_score: number
  pass_rate: number
  avg_confidence: number
  last_run: string
  rank: number
}

interface LeaderboardResponse {
  rankings: AgentRanking[]
  generated_at: string
  total_submissions: number
  total_agents: number
}

const PASS_THRESHOLD = 60

// Medal styling for the top 3 ranks.
const medalConfig: Record<number, { icon: ReactNode; color: string; bg: string }> = {
  1: { icon: <Crown size={18} />, color: '#FFD700', bg: 'rgba(255,215,0,0.10)' },
  2: { icon: <Medal size={18} />, color: '#C0C0C0', bg: 'rgba(192,192,192,0.10)' },
  3: { icon: <Medal size={18} />, color: '#CD7F32', bg: 'rgba(205,127,50,0.10)' },
}

// Tolerantly normalize an API payload into the expected shape.
function normalizeLeaderboard(res: unknown): LeaderboardResponse {
  if (res && typeof res === 'object') {
    const obj = res as Record<string, unknown>
    const data = (obj.data && typeof obj.data === 'object' ? obj.data : obj) as Record<string, unknown>
    const rankings = Array.isArray(data.rankings) ? data.rankings as AgentRanking[] : []
    return {
      rankings,
      generated_at: typeof data.generated_at === 'string' ? data.generated_at : new Date().toISOString(),
      total_submissions: typeof data.total_submissions === 'number' ? data.total_submissions : 0,
      total_agents: typeof data.total_agents === 'number' ? data.total_agents : rankings.length,
    }
  }
  return { rankings: [], generated_at: new Date().toISOString(), total_submissions: 0, total_agents: 0 }
}

function formatTime(dateStr: string, lang: 'zh' | 'en'): string {
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleString(lang === 'zh' ? 'zh-CN' : 'en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function scoreColor(score: number): string {
  if (score >= 80) return 'var(--color-success)'
  if (score >= 60) return 'var(--color-warning)'
  return 'var(--color-danger)'
}

function confidenceColor(c: number): string {
  if (c >= 0.8) return 'var(--color-success)'
  if (c >= 0.5) return 'var(--color-warning)'
  return 'var(--color-danger)'
}

export default function LeaderboardPage() {
  const { t, lang } = useLang()
  const [data, setData] = useState<LeaderboardResponse>({ rankings: [], generated_at: '', total_submissions: 0, total_agents: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Use the server-side aggregation endpoint. No client-side mock fallback —
      // the leaderboard only shows anti-cheat-accepted submissions.
      const res = await apiGet<unknown>('/leaderboard')
      setData(normalizeLeaderboard(res))
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    loadData()
  }, [loadData])

  // ---- Loading state ----
  if (loading) {
    return (
      <div className="container-page px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
        <div className="flex flex-col items-center justify-center py-24">
          <Loader className="animate-spin" size={32} style={{ color: 'var(--color-accent)' }} />
          <span className="mt-3 text-lg" style={{ color: 'var(--color-text-secondary)' }}>
            {t('common.loading')}
          </span>
        </div>
      </div>
    )
  }

  // ---- Error state ----
  if (error) {
    return (
      <div className="container-page px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
        <div className="flex flex-col items-center justify-center py-24">
          <AlertCircle size={48} style={{ color: 'var(--color-danger)' }} />
          <p className="mt-4 text-lg" style={{ color: 'var(--color-danger)' }}>{t('common.error')}</p>
          <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>{error}</p>
          <button
            onClick={loadData}
            className="mt-4 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
          >
            <RefreshCw size={15} /> {t('common.retry')}
          </button>
        </div>
      </div>
    )
  }

  const rankings = data.rankings

  // ---- Main view ----
  return (
    <div className="container-page px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: 'var(--color-accent-glow)' }}
        >
          <Trophy size={22} style={{ color: 'var(--color-accent)' }} />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{t('lb.title')}</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>{t('lb.subtitle')}</p>
        </div>
      </div>

      {/* Verified-only banner */}
      <div
        className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg text-xs"
        style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}
      >
        <ShieldCheck size={14} style={{ color: 'var(--color-success)' }} />
        <span>{t('lb.verifiedOnly')}</span>
        <span className="ml-auto" style={{ color: 'var(--color-text-muted)' }}>
          {t('lb.submissions')}: {data.total_submissions} · {data.total_agents} agents
        </span>
      </div>

      {/* Leaderboard table */}
      <div
        className="rounded-xl overflow-hidden"
        style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}
      >
        {rankings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Award size={40} style={{ color: 'var(--color-text-muted)' }} />
            <p className="mt-3 text-base" style={{ color: 'var(--color-text-muted)' }}>{t('lb.noData')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>{t('lb.rank')}</th>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>{t('lb.agent')}</th>
                  <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>{t('lb.score')}</th>
                  <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>{t('lb.sessions')}</th>
                  <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>{t('lb.avgScore')}</th>
                  <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>{t('lb.passRate')}</th>
                  <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>{t('lb.confidence')}</th>
                  <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>{t('lb.lastRun')}</th>
                </tr>
              </thead>
              <tbody>
                {rankings.map((r) => {
                  const rank = r.rank
                  const medal = medalConfig[rank]
                  const conf = r.avg_confidence ?? 0
                  return (
                    <tr
                      key={r.agent_name}
                      className="transition-colors hover:opacity-80"
                      style={{
                        borderBottom: '1px solid var(--color-border)',
                        backgroundColor: medal ? medal.bg : 'transparent',
                      }}
                    >
                      {/* Rank */}
                      <td className="px-4 py-3">
                        {medal ? (
                          <span style={{ color: medal.color }}>{medal.icon}</span>
                        ) : (
                          <span className="text-sm font-semibold" style={{ color: 'var(--color-text-muted)' }}>#{rank}</span>
                        )}
                      </td>

                      {/* Agent */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                            style={{
                              backgroundColor: medal ? medal.bg : 'var(--color-bg-tertiary)',
                              color: medal ? medal.color : 'var(--color-text-secondary)',
                            }}
                          >
                            {r.agent_name.charAt(0).toUpperCase() || '?'}
                          </div>
                          <span className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                            {r.agent_name}
                          </span>
                        </div>
                      </td>

                      {/* Total Score */}
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm font-bold font-mono" style={{ color: 'var(--color-accent)' }}>
                          {r.total_score.toFixed(0)}
                        </span>
                      </td>

                      {/* Sessions */}
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{r.sessions}</span>
                      </td>

                      {/* Avg Score */}
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm font-mono" style={{ color: scoreColor(r.avg_score) }}>
                          {r.avg_score.toFixed(1)}
                        </span>
                      </td>

                      {/* Pass Rate */}
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <div
                            className="w-16 h-1.5 rounded-full overflow-hidden"
                            style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
                          >
                            <div
                              className="h-full rounded-full"
                              style={{ width: `${r.pass_rate}%`, backgroundColor: scoreColor(r.pass_rate) }}
                            />
                          </div>
                          <span className="text-sm font-mono" style={{ color: 'var(--color-text-secondary)' }}>
                            {r.pass_rate.toFixed(0)}%
                          </span>
                        </div>
                      </td>

                      {/* Confidence */}
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm font-mono" style={{ color: confidenceColor(conf) }}>
                          {(conf * 100).toFixed(0)}%
                        </span>
                      </td>

                      {/* Last Run */}
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                          {r.last_run ? formatTime(r.last_run, lang) : '—'}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
